import React, { useMemo, useState } from 'react';
import { UserIcon, CalendarIcon, PlayIcon, DownloadIcon, FileTextIcon, ActivityIcon, LogInIcon, LayoutDashboardIcon } from 'lucide-react';
import { BusinessHistory, BusinessEvent } from '../../common/BusinessHistory';
interface UserAuditReportProps {
  locale: string;
  report: any;
}
const mockUsers = [{
  id: 'USR-001',
  name: 'John Smith',
  department: 'Finance'
}, {
  id: 'USR-002',
  name: 'Sarah Chen',
  department: 'Accounts Payable'
}, {
  id: 'USR-003',
  name: 'Mike Johnson',
  department: 'Procurement'
}, {
  id: 'USR-004',
  name: 'Emily Davis',
  department: 'Finance'
}, {
  id: 'USR-005',
  name: 'Robert Wilson',
  department: 'IT'
}, {
  id: 'USR-006',
  name: 'System Admin',
  department: 'Administration'
}];
// Event templates for generating realistic audit data
const eventTemplates = {
  authentication: [{
    description: 'Logged in',
    details: 'Successful login from IP 192.168.1.{ip}'
  }, {
    description: 'Logged out',
    details: 'Session ended after {duration} minutes of activity'
  }, {
    description: 'Password changed',
    details: 'Password updated successfully'
  }],
  invoice: [{
    description: 'Created invoice INV-{num}',
    details: 'New invoice created for vendor {vendor}, amount ${amount}'
  }, {
    description: 'Reviewed invoice INV-{num}',
    details: 'Invoice reviewed and validated, {lines} line items checked'
  }, {
    description: 'Approved invoice INV-{num}',
    details: 'Invoice approved for payment, routed to payment queue'
  }, {
    description: 'Rejected invoice INV-{num}',
    details: 'Invoice rejected due to {reason}'
  }, {
    description: 'Applied GL coding to INV-{num}',
    details: 'GL codes applied: {glCode} for ${amount}'
  }],
  procurement: [{
    description: 'Created purchase order PO-{num}',
    details: 'PO created for {vendor}, total value ${amount}'
  }, {
    description: 'Sent PO-{num} to vendor',
    details: 'Purchase order emailed to {vendor}'
  }, {
    description: 'Approved requisition REQ-{num}',
    details: 'Requisition approved, ready for PO conversion'
  }, {
    description: 'Received goods for PO-{num}',
    details: 'Goods receipt recorded, {qty} items received'
  }],
  vendor: [{
    description: 'Onboarded vendor {vendor}',
    details: 'New vendor created with ABN {abn}'
  }, {
    description: 'Updated vendor {vendor}',
    details: 'Updated contact information and payment terms'
  }, {
    description: 'Completed compliance check for {vendor}',
    details: 'Compliance verification passed, all documents valid'
  }, {
    description: 'Verified bank details for {vendor}',
    details: 'Bank account verified via external service'
  }],
  document: [{
    description: 'Uploaded document {docName}',
    details: 'File uploaded to {folder}, size {size}KB'
  }, {
    description: 'Reviewed document {docName}',
    details: 'Document reviewed and marked as processed'
  }, {
    description: 'Extracted data from {docName}',
    details: 'OCR extraction completed with {confidence}% confidence'
  }],
  settings: [{
    description: 'Updated tax code {taxCode}',
    details: 'Tax rate changed from {oldRate}% to {newRate}%'
  }, {
    description: 'Modified workflow rule',
    details: 'Approval threshold updated for {entity}'
  }, {
    description: 'Changed user permissions',
    details: 'Updated role assignments for {targetUser}'
  }],
  reporting: [{
    description: 'Generated {reportName} report',
    details: 'Report generated for period {period}'
  }, {
    description: 'Exported data to {format}',
    details: 'Exported {count} records to {format} format'
  }, {
    description: 'Scheduled report {reportName}',
    details: 'Report scheduled for {frequency} delivery'
  }]
};
const vendors = ['Acme Corp', 'Office Depot', 'Dell Technologies', 'Server Solutions Inc.', 'PrintWorks Inc.'];
const glCodes = ['6100-100', '6200-200', '5100-300', '4100-400', '7100-500'];
const reasons = ['missing PO reference', 'price variance exceeded tolerance', 'duplicate invoice detected', 'invalid tax calculation'];
const reportNames = ['Invoice Aging', 'Budget vs Actual', 'Vendor Spend Analysis', 'PO Summary'];
const docNames = ['Invoice_2024_001.pdf', 'Contract_Amendment.docx', 'Receipt_Scan.jpg', 'Statement_Q4.pdf'];
function generateRandomEvents(userName: string, startDate: Date, endDate: Date): BusinessEvent[] {
  const events: BusinessEvent[] = [];
  const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  const numEvents = Math.min(25, Math.max(15, Math.floor(daysDiff * 0.8)));
  // Generate timestamps spread across the date range
  const timestamps: Date[] = [];
  for (let i = 0; i < numEvents; i++) {
    const randomDay = Math.floor(Math.random() * daysDiff);
    const randomHour = 8 + Math.floor(Math.random() * 10); // 8am to 6pm
    const randomMinute = Math.floor(Math.random() * 60);
    const timestamp = new Date(startDate);
    timestamp.setDate(timestamp.getDate() + randomDay);
    timestamp.setHours(randomHour, randomMinute, 0, 0);
    timestamps.push(timestamp);
  }
  // Sort timestamps chronologically (newest first for display)
  timestamps.sort((a, b) => b.getTime() - a.getTime());
  const categories = Object.keys(eventTemplates);
  timestamps.forEach((timestamp, index) => {
    // Weight towards more common activities
    const categoryWeights = {
      invoice: 0.25,
      procurement: 0.2,
      document: 0.15,
      authentication: 0.15,
      vendor: 0.1,
      reporting: 0.1,
      settings: 0.05
    };
    let category: string;
    const rand = Math.random();
    let cumulative = 0;
    for (const [cat, weight] of Object.entries(categoryWeights)) {
      cumulative += weight;
      if (rand <= cumulative) {
        category = cat;
        break;
      }
    }
    category = category || 'invoice';
    const templates = eventTemplates[category as keyof typeof eventTemplates];
    const template = templates[Math.floor(Math.random() * templates.length)];
    // Replace placeholders with random values
    let description = template.description;
    let details = template.details;
    const replacements: Record<string, string> = {
      '{num}': String(2024000 + Math.floor(Math.random() * 999)).padStart(3, '0'),
      '{vendor}': vendors[Math.floor(Math.random() * vendors.length)],
      '{amount}': (Math.floor(Math.random() * 50000) + 500).toLocaleString(),
      '{lines}': String(Math.floor(Math.random() * 10) + 1),
      '{reason}': reasons[Math.floor(Math.random() * reasons.length)],
      '{glCode}': glCodes[Math.floor(Math.random() * glCodes.length)],
      '{ip}': String(Math.floor(Math.random() * 254) + 1),
      '{duration}': String(Math.floor(Math.random() * 180) + 15),
      '{abn}': `${Math.floor(Math.random() * 90) + 10} ${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 900) + 100}`,
      '{qty}': String(Math.floor(Math.random() * 100) + 1),
      '{docName}': docNames[Math.floor(Math.random() * docNames.length)],
      '{folder}': ['Invoices', 'Contracts', 'Receipts', 'Statements'][Math.floor(Math.random() * 4)],
      '{size}': String(Math.floor(Math.random() * 5000) + 100),
      '{confidence}': String(Math.floor(Math.random() * 15) + 85),
      '{taxCode}': ['GST', 'FRE', 'CAP', 'INP'][Math.floor(Math.random() * 4)],
      '{oldRate}': String(Math.floor(Math.random() * 5) + 8),
      '{newRate}': String(Math.floor(Math.random() * 5) + 10),
      '{entity}': ['Invoices', 'Purchase Orders', 'Requisitions'][Math.floor(Math.random() * 3)],
      '{targetUser}': mockUsers[Math.floor(Math.random() * mockUsers.length)].name,
      '{reportName}': reportNames[Math.floor(Math.random() * reportNames.length)],
      '{period}': ['Q4 2024', 'November 2024', 'Last 30 days'][Math.floor(Math.random() * 3)],
      '{format}': ['CSV', 'PDF', 'Excel'][Math.floor(Math.random() * 3)],
      '{count}': String(Math.floor(Math.random() * 500) + 50),
      '{frequency}': ['daily', 'weekly', 'monthly'][Math.floor(Math.random() * 3)]
    };
    for (const [placeholder, value] of Object.entries(replacements)) {
      description = description.replace(placeholder, value);
      details = details.replace(placeholder, value);
    }
    events.push({
      id: `evt-${index + 1}`,
      timestamp: timestamp.toISOString(),
      description,
      actor: userName,
      actorType: 'user',
      category,
      details
    });
  });
  return events;
}
export function UserAuditReport({
  locale,
  report
}: UserAuditReportProps) {
  const [selectedUserId, setSelectedUserId] = useState('USR-001');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [hasRun, setHasRun] = useState(false);
  const selectedUser = mockUsers.find(u => u.id === selectedUserId) || mockUsers[0];
  const events = useMemo(() => {
    if (!hasRun) return [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    return generateRandomEvents(selectedUser.name, start, end);
  }, [hasRun, selectedUserId, startDate, endDate, selectedUser.name]);
  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!hasRun || events.length === 0) {
      return {
        totalActions: 0,
        mostActiveModule: '-',
        sessions: 0
      };
    }
    const categoryCounts: Record<string, number> = {};
    let loginCount = 0;
    events.forEach(event => {
      categoryCounts[event.category] = (categoryCounts[event.category] || 0) + 1;
      if (event.description.includes('Logged in')) {
        loginCount++;
      }
    });
    const categoryLabels: Record<string, string> = {
      invoice: 'Accounts Payable',
      procurement: 'Procurement',
      vendor: 'Vendor Management',
      document: 'Document Intelligence',
      settings: 'Settings',
      reporting: 'Reporting',
      authentication: 'Authentication'
    };
    let mostActiveCategory = '';
    let maxCount = 0;
    for (const [cat, count] of Object.entries(categoryCounts)) {
      if (count > maxCount && cat !== 'authentication') {
        maxCount = count;
        mostActiveCategory = cat;
      }
    }
    return {
      totalActions: events.length,
      mostActiveModule: categoryLabels[mostActiveCategory] || 'N/A',
      sessions: Math.max(1, loginCount)
    };
  }, [hasRun, events]);
  const handleRunReport = () => {
    setHasRun(true);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  return <div className="p-6 space-y-6" data-id="element-15855">
      {/* Filters Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-15856">
        <div className="flex flex-wrap items-end gap-4" data-id="element-15857">
          <div className="flex-1 min-w-[200px]" data-id="element-15858">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15859">
              <UserIcon className="inline-block w-4 h-4 mr-1" data-id="element-15860" />
              User
            </label>
            <select value={selectedUserId} onChange={e => {
            setSelectedUserId(e.target.value);
            setHasRun(false);
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-15861">
              {mockUsers.map(user => <option key={user.id} value={user.id} data-id="element-15862">
                  {user.name} ({user.department})
                </option>)}
            </select>
          </div>

          <div className="min-w-[160px]" data-id="element-15863">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15864">
              <CalendarIcon className="inline-block w-4 h-4 mr-1" data-id="element-15865" />
              Start Date
            </label>
            <input type="date" value={startDate} onChange={e => {
            setStartDate(e.target.value);
            setHasRun(false);
          }} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-15866" />
          </div>

          <div className="min-w-[160px]" data-id="element-15867">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15868">
              <CalendarIcon className="inline-block w-4 h-4 mr-1" data-id="element-15869" />
              End Date
            </label>
            <input type="date" value={endDate} onChange={e => {
            setEndDate(e.target.value);
            setHasRun(false);
          }} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-15870" />
          </div>

          <button onClick={handleRunReport} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15871">
            <PlayIcon className="w-4 h-4 mr-2" data-id="element-15872" />
            Run Report
          </button>
        </div>
      </div>

      {!hasRun /* Placeholder before running */ ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-15873">
          <ActivityIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-15874" />
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-15875">
            User Audit Trail
          </h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto" data-id="element-15876">
            Select a user and date range, then click <strong data-id="element-15877">Run Report</strong>{' '}
            to view their activity timeline.
          </p>
        </div> : <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-15878">
            <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-15879">
              <div className="flex items-center" data-id="element-15880">
                <div className="flex-shrink-0 bg-indigo-100 rounded-lg p-3" data-id="element-15881">
                  <ActivityIcon className="h-6 w-6 text-indigo-600" data-id="element-15882" />
                </div>
                <div className="ml-4" data-id="element-15883">
                  <p className="text-sm font-medium text-gray-500" data-id="element-15884">
                    Total Actions
                  </p>
                  <p className="text-2xl font-semibold text-gray-900" data-id="element-15885">
                    {summaryStats.totalActions}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-15886">
              <div className="flex items-center" data-id="element-15887">
                <div className="flex-shrink-0 bg-green-100 rounded-lg p-3" data-id="element-15888">
                  <LayoutDashboardIcon className="h-6 w-6 text-green-600" data-id="element-15889" />
                </div>
                <div className="ml-4" data-id="element-15890">
                  <p className="text-sm font-medium text-gray-500" data-id="element-15891">
                    Most Active Module
                  </p>
                  <p className="text-2xl font-semibold text-gray-900" data-id="element-15892">
                    {summaryStats.mostActiveModule}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-15893">
              <div className="flex items-center" data-id="element-15894">
                <div className="flex-shrink-0 bg-blue-100 rounded-lg p-3" data-id="element-15895">
                  <LogInIcon className="h-6 w-6 text-blue-600" data-id="element-15896" />
                </div>
                <div className="ml-4" data-id="element-15897">
                  <p className="text-sm font-medium text-gray-500" data-id="element-15898">Sessions</p>
                  <p className="text-2xl font-semibold text-gray-900" data-id="element-15899">
                    {summaryStats.sessions}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white border border-gray-200 rounded-lg" data-id="element-15900">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-15901">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-15902">
                Activity Timeline for {selectedUser.name}
              </h3>
              <p className="text-sm text-gray-500" data-id="element-15903">
                {formatDate(startDate)} — {formatDate(endDate)}
              </p>
            </div>
            <div className="p-6" data-id="element-15904">
              <BusinessHistory entityType="User" entityId={selectedUser.id} events={events} data-id="element-15905" />
            </div>
          </div>

          {/* Export Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-id="element-15906">
            <p className="text-sm text-gray-600" data-id="element-15907">
              <strong data-id="element-15908">{events.length}</strong> events found for{' '}
              {selectedUser.name} between {formatDate(startDate)} and{' '}
              {formatDate(endDate)}
            </p>
            <div className="flex gap-2" data-id="element-15909">
              <button className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15910">
                <DownloadIcon className="w-4 h-4 mr-2" data-id="element-15911" />
                Export to CSV
              </button>
              <button className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15912">
                <FileTextIcon className="w-4 h-4 mr-2" data-id="element-15913" />
                Export to PDF
              </button>
            </div>
          </div>
        </>}
    </div>;
}